import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { Alert, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";

interface AlertMessage {
  id: string;
  message: string;
  variant?: "danger" | "warning" | "info" | "success";
  type?: "alert" | "modal" | "confirm";
  title?: string;
  confirmButtonText?: string;
  closeButtonText?: string;
  onConfirm?: () => void;
}

interface AlertContextType {
  showAlert: (
    message: string,
    variant?: "danger" | "warning" | "info" | "success",
  ) => void;
  showAlertModal: (
    message: string,
    title?: string,
    closeButtonText?: string,
    warning?: boolean,
  ) => void;
  showConfirmModal: (
    message: string,
    onConfirm: () => void,
    title?: string,
    confirmButtonText?: string,
    closeButtonText?: string,
  ) => void;
  dismissAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider = ({ children }: AlertProviderProps) => {
  const [alerts, setAlerts] = useState<AlertMessage[]>([]);
  const [modalAlert, setModalAlert] = useState<AlertMessage | null>(null);

  const showAlert = useCallback(
    (
      message: string,
      variant: "danger" | "warning" | "info" | "success" = "danger",
    ) => {
      const id = Date.now().toString();
      const newAlert: AlertMessage = {
        id,
        message,
        variant,
        type: "alert",
      };

      setAlerts((prev: AlertMessage[]) => [...prev, newAlert]);

      setTimeout(() => {
        setAlerts((prev) =>
          prev.filter((alert: AlertMessage) => alert.id !== id),
        );
      }, 5000);
    },
    [],
  );

  const showAlertModal = useCallback(
    (
      message: string,
      title: string = "Hinweis",
      closeButtonText: string = "Schließen",
      warning: boolean = false,
    ) => {
      const id = Date.now().toString();
      const newAlert: AlertMessage = {
        id,
        message,
        title,
        closeButtonText,
        variant: warning ? "warning" : "info",
        type: "modal",
      };

      setModalAlert(newAlert);
    },
    [],
  );

  const showConfirmModal = useCallback(
    (
      message: string,
      onConfirm: () => void,
      title: string = "Bestätigung",
      confirmButtonText: string = "Ja",
      closeButtonText: string = "Abbrechen",
    ) => {
      const id = Date.now().toString();
      const newAlert: AlertMessage = {
        id,
        message,
        title,
        confirmButtonText,
        closeButtonText,
        type: "confirm",
        onConfirm,
      };
      setModalAlert(newAlert);
    },
    [],
  );

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert: AlertMessage) => alert.id !== id));
  }, []);

  const closeModal = useCallback(() => {
    setModalAlert(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      showAlert,
      showAlertModal,
      showConfirmModal,
      dismissAlert,
    }),
    [showAlert, showAlertModal, showConfirmModal, dismissAlert],
  );

  return (
    <AlertContext.Provider value={contextValue}>
      {children}

      {/* toast style alerts*/}
      <div
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 9999,
          maxWidth: "400px",
        }}
      >
        {alerts.length > 0 &&
          alerts.map((alert: AlertMessage) => (
            <Alert
              key={alert.id}
              variant={alert.variant}
              dismissible
              onClose={() => dismissAlert(alert.id)}
              className="mb-2"
            >
              {alert.message}
            </Alert>
          ))}
      </div>

      {/**modal alert */}
      {modalAlert && (
        <Modal
          show={true}
          onHide={closeModal}
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>{modalAlert.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body
            style={{
              whiteSpace: "pre-line",
              color: modalAlert.variant === "warning" ? "#fd7e14" : undefined,
            }}
          >
            {modalAlert.message}
          </Modal.Body>
          <Modal.Footer>
            {modalAlert.type === "confirm" && (
              <Button
                variant="primary"
                onClick={() => {
                  modalAlert.onConfirm?.();
                  closeModal();
                }}
              >
                {modalAlert.confirmButtonText || "Ja"}
              </Button>
            )}
            <Button
              variant={modalAlert.type === "confirm" ? "secondary" : "primary"}
              onClick={closeModal}
            >
              {modalAlert.closeButtonText || "Schließen"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

export type { AlertContextType };
